import { CompositeChart } from '@mantine/charts';
import classes from '../../../app.module.css';
import { Paper, Space, Stack, Title, Text, Flex } from '@mantine/core';
import { RefObject } from 'react';
import { chartAxisDateFormatter, getAxisWidth } from '../util';
import { PercentageChange } from './percentageChange';
import { comparisonParameterHistoryData } from '../stores';
import { useTranslation } from 'react-i18next';

export const ComparisonParameterHistory = ({
  ref,
  parameter,
  data,
  unit,
  infoText,
  maxWidth,
}: {
  ref?: RefObject<HTMLDivElement | null>;
  parameter: string;
  data: comparisonParameterHistoryData[];
  unit: string;
  infoText: string;
  maxWidth: number;
}) => {
  const { t } = useTranslation(['reports']);
  const refinedData = data.map((entry) => {
    return {
      date: entry.date,
      series: entry.series,
      line1: entry.series[0],
      line2: entry.series[1],
    };
  });

  function calculateAxisWidth(data: typeof refinedData) {
    const maxValue = () => {
      let max = 0;
      for (const entry of data) {
        if (max < entry.line1) {
          max = entry.line1;
        }
      }
      return max;
    };
    return getAxisWidth(maxValue());
  }

  return (
    <Stack ref={ref} gap={24} style={{ scrollSnapAlign: 'center' }}>
      <Paper style={{ padding: 24 }}>
        <Title
          order={5}
          style={{
            width: `calc(min(100vw, ${maxWidth}px) - (2 * var(--mantine-spacing-md) + 48px)`,
          }}
        >
          {t(`reports:${parameter}`)}
        </Title>
        <Flex direction={'row'}>
          <Title style={{ paddingRight: 'var(--mantine-spacing-xs)' }}>
            {`${data[data.length - 1].series[0]}/${data[data.length - 1].series[1]}`}{' '}
            {unit}
          </Title>
          {data.length > 1 && (
            <PercentageChange
              currentValue={data[data.length - 1].series[0]}
              previousValue={data[data.length - 2].series[0]}
            />
          )}
        </Flex>
        <Space h={'var(--mantine-spacing-md)'} />
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient
              id="comparisonChartGradient"
              x1="0%"
              y1="0%"
              x2="0%"
              y2="100%"
            >
              <stop
                offset="0%"
                stopColor="var(--mantine-primary-color-0)"
                stopOpacity={0.8}
              />
              <stop
                offset="100%"
                stopColor="var(--mantine-color-innovationPurple-0)"
                stopOpacity={0.8}
              />
            </linearGradient>
          </defs>
        </svg>
        <CompositeChart
          h={300}
          data={refinedData}
          dataKey="date"
          withTooltip={false}
          withDots={false}
          tickLine="x"
          series={[
            {
              name: `series`,
              color: 'var(--mantine-primary-color-0)',
              type: 'area',
            },
            {
              name: `line1`,
              color: 'var(--mantine-primary-color-0)',
              type: 'line',
            },
            {
              name: `line2`,
              color: 'var(--mantine-color-innovationPurple-0)',
              type: 'line',
            },
          ]}
          curveType="linear"
          strokeWidth={3}
          className={classes.areaChart}
          areaProps={{
            stroke: '#00000000',
            fill: 'url(#comparisonChartGradient)',
          }}
          xAxisProps={{
            padding: { left: 24, right: 24 },
            tickFormatter: (value: Date) => chartAxisDateFormatter(value),
            tickLine: { stroke: 'var(--mantine-color-otherGrey10-0)' },
          }}
          yAxisProps={{
            width: calculateAxisWidth(refinedData),
            allowDecimals: false,
            domain: ([dataMin, dataMax]) => {
              return [
                dataMin < 60 ? dataMin - 10 : 60,
                dataMax > 140 ? dataMax + 10 : 140,
              ];
            },
          }}
        ></CompositeChart>
      </Paper>
      <Paper>
        <Title order={5}>{t(`reports:${parameter}`)}</Title>
        <Space h={'var(--mantine-spacing-sm)'} />
        <Text>{infoText}</Text>
      </Paper>
    </Stack>
  );
};
